import type { DashboardProjection, SalonSnapshot } from './contracts.js';

const localDateKey = (iso: string, timezone: string): string => new Intl.DateTimeFormat('en-CA', {
  timeZone: timezone,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
}).format(new Date(iso));

export function projectDashboard(snapshot: SalonSnapshot): DashboardProjection {
  const today = snapshot.generatedForDate;
  const timezone = snapshot.branch.timezone;
  const todaysAppointments = snapshot.appointments.filter((appointment) => localDateKey(appointment.startAt, timezone) === today && appointment.status !== 'cancelled');
  const completed = todaysAppointments.filter((appointment) => appointment.status === 'completed');
  const completedCheckoutIds = new Set(snapshot.checkoutDrafts.filter((checkout) => checkout.status === 'completed').map((checkout) => checkout.id));
  const estimatedRevenueMinor = snapshot.financePayments.filter((payment) => payment.status === 'approved' && completedCheckoutIds.has(payment.checkoutId)).reduce((total, payment) => total + payment.amountMinor, 0);
  const startOfToday = new Date(`${today}T00:00:00`);
  const upcomingAppointments = snapshot.appointments
    .filter((appointment) => new Date(appointment.startAt) >= startOfToday && appointment.status !== 'cancelled' && appointment.status !== 'completed')
    .sort((left, right) => left.startAt.localeCompare(right.startAt))
    .slice(0, 6);
  const staffOccupancy = snapshot.staff.map((staff) => {
    const appointmentCount = todaysAppointments.filter((appointment) => appointment.staffId === staff.id).length;
    return {
      staffId: staff.id,
      staffName: staff.displayName,
      appointmentCount,
      utilizationPercent: Math.min(100, appointmentCount * 22)
    };
  });
  const returningToday = todaysAppointments.filter((appointment) => {
    const client = snapshot.clients.find((entry) => entry.partyId === appointment.clientPartyId);
    return (client?.totalVisits ?? 0) > 1;
  }).length;

  return {
    todayAppointments: todaysAppointments.length,
    waitingClients: snapshot.waitingList.filter((entry) => entry.status === 'waiting').length,
    activeServices: todaysAppointments.filter((appointment) => appointment.status === 'in_service').length,
    estimatedRevenueMinor,
    completedServices: completed.length,
    upcomingAppointments,
    staffOccupancy,
    walkInWaiting: snapshot.waitingList.filter((entry) => entry.status === 'waiting').length,
    returningToday
  };
}
